import Box from '@components/Box';
import NoItems from '@components/NoItems';
import { NoData, INoDataProps } from "@components/new/NoData";
import Pagination from '@components/Pagination';
import { FilterModel } from '@hooks/useFiltersSearchParams';
import { Grid, Row, Column } from '@components/Grid';
import { Loader } from '@components/Loader';
import { SearchInfo } from './components/SearchInfo';
import { useLocation } from 'react-router-dom';
import { usePagingSearchParams, PaginationModel } from '@hooks/usePagingSearchParams';
import { useQuery } from 'react-query';
import { useState, useEffect, useImperativeHandle, ReactNode } from 'react';

export interface BeforeProps<T, Params=void> {
  items: T[];
  isLoading: boolean;
  total: number;
  filters?: FilterModel[];
  params?: Params;
}

export type TListenerTableData<T> = (data: T[], total: number) => void;

export type DataViewHandle<T> = {
  refresh: () => void,
  total: number,
  data?: T[]
}

export type DataViewProps<T, VP, BP=void, AP=void> = {
  pending?: boolean;
  queryKey?: string | string[];
  getData: (data: {
    page: PaginationModel,
    filters?: FilterModel[],
    sorting?: { column: string, direction: string };
  }) => Promise<({ items: T[], total: number })>;
  notFound?: string | React.FC;
  notFoundDescription?: string;
  filters?: FilterModel[];
  sorting?: { column: string, direction: string },
  Before?: React.FC<BeforeProps<T, BP>>;
  After?: React.FC<BeforeProps<T, AP>>;
  View: React.FC<any>; // ViewExtendedProps<T, ViewProps>
  viewProps?: VP;
  beforeProps?: BP;
  afterProps?: AP;
  defaultPageSize?: number;
  dataRef?: React.RefObject<DataViewHandle<T>>;
  withPagination?: boolean;
  onUpdate?: TListenerTableData<T>;
  noLoaderOnRefresh?: boolean;
  noDataProps?: INoDataProps;
  skeleton?: ReactNode;
}

const MAX_PAGE_SIZE = 99999;

function DataView<T, ViewProps, BP = void, AP = void>({
  pending,
  getData,
  queryKey,
  notFound,
  notFoundDescription,
  filters,
  sorting,
  Before,
  beforeProps,
  After,
  afterProps,
  View,
  viewProps,
  defaultPageSize,
  dataRef,
  withPagination,
  onUpdate,
  noLoaderOnRefresh,
  noDataProps,
  skeleton,
}: DataViewProps<T, ViewProps, BP, AP>) {
  const [currentPage, setCurrentPage] = usePagingSearchParams(1, defaultPageSize || 10);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [viewKey, setViewKey] = useState(1);

  const { pathname } = useLocation();

  const showPagination = withPagination ?? true;

  const query = useQuery({
    queryKey: [
      ...(Array.isArray(queryKey) ? queryKey : [queryKey]),
      currentPage.page,
      currentPage.pageSize,
      JSON.stringify(filters),
      JSON.stringify(sorting),
    ],
    queryFn: () => getData({
      page: currentPage,
      filters,
      sorting,
    }),
    onSuccess: () => { setViewKey(viewKey + 1) },
    retry: 1,
    enabled: !pending,
    staleTime: 0,
    // cacheTime: 0,
  });
  const { items, total: totalItems } = query.data || { items: [], total: 0 };
  const isLoading = pending || (noLoaderOnRefresh ? query.isLoading : query.isFetching);

  useImperativeHandle(dataRef, () => ({
    refresh: () => {
      query.refetch();
    },
    total: query.data?.total ?? 0,
    data: query.data?.items,
  }));

  const updatePagination = (page: number, pageSize: number) => {
    setCurrentPage({ page, pageSize });
  }

  useEffect(() => {
    if (!pending) {
      setIsInitialized(true);
    }
  }, [pending]);

  useEffect(() => {
    if (isInitialized) {
      const condition: boolean = pathname === '/pick-up';
      const trackNumberFilter: FilterModel | undefined = (filters || []).find((filter: FilterModel) => filter.name === 'trackOrExternalNumbers' || filter.name === 'trackNumbers');

      setCurrentPage({
        page: 1,
        pageSize: condition && trackNumberFilter && trackNumberFilter.value.length && trackNumberFilter.value[0].split(',').length > 1
            ? MAX_PAGE_SIZE
            : currentPage.pageSize === MAX_PAGE_SIZE
                ? defaultPageSize ?? 10
                : currentPage.pageSize
      });
    }
  }, [isInitialized, pathname, JSON.stringify(filters)]);

  useEffect(() => {
    if (
      query.data?.total
      && !query.data?.items?.length
      && currentPage.page > 1
    ) {
      setCurrentPage({ page: 1, pageSize: currentPage.pageSize });
    }
    if (window.location.search.includes('tab=awaiting')) {
      localStorage.setItem('awaitingParcels', JSON.stringify(items.map((item: any) => item.id)));
    }
    if (window.location.search.includes('tab=disposal')) {
      localStorage.setItem('disposalParcels', JSON.stringify(items.map((item: any) => item.id)));
    }
    if (window.location.search.includes('tab=resale')) {
      localStorage.setItem('resaleParcels', JSON.stringify(items.map((item: any) => item.id)));
    }
    if (window.location.search.includes('tab=returns')) {
      localStorage.setItem('returnsParcels', JSON.stringify(items.map((item: any) => item.id)));
    }
    onUpdate && onUpdate(query.data?.items ?? [], query.data?.total ?? 0);
  }, [query.data?.total, query.data?.items?.length]);

  if (isLoading) {
    return (
      <>
        {Before && (
          <Before
            items={items ?? []}
            filters={filters}
            total={query.data?.total || 0}
            isLoading={isLoading}
            params={beforeProps}
          />
        )}
        {skeleton ? (
          skeleton
        ) : (
          <Grid>
            <Row>
              <Column phone={12}>
                <Loader alignment="center" size="s" />
              </Column>
            </Row>
          </Grid>
        )}
        {After && (
          <After
            items={items ?? []}
            filters={filters}
            total={query.data?.total || 0}
            isLoading={isLoading}
            params={afterProps}
          />
        )}
      </>
    )
  }

  if (items && !isLoading && !items.length) {
    const NotFound = notFound;
    return (
      <>
        {Before && (
          <Before
            items={items ?? []}
            filters={filters}
            total={query.data?.total || 0}
            isLoading={isLoading}
            params={beforeProps}
          />
        )}
        {noDataProps ? (
          <NoData
            title={noDataProps.title}
            description={noDataProps.description}
            btnTitle={noDataProps.btnTitle}
            onClick={noDataProps.onClick}
            width={noDataProps.width}
          />
        ) : (
          (NotFound && typeof NotFound === "function") ? (
            <NotFound />
          ) : (
            <NoItems
              title={NotFound || ""}
              description={notFoundDescription || ""}
            />
          )
        )}
        {}
        {After && (
          <After
            items={items ?? []}
            filters={filters}
            total={query.data?.total || 0}
            isLoading={isLoading}
            params={afterProps}
          />
        )}
      </>
    )
  }

  return (
    <>
      {Before && (
        <Before
          items={items ?? []}
          filters={filters}
          total={query.data?.total || 0}
          isLoading={isLoading}
          params={beforeProps}
        />
      )}
      <View
        key={`data-view-${viewKey}`}
        {...viewProps}
        data={items ?? [] as T[]}
        filters={filters}
      />
      <SearchInfo
        items={items ?? []}
        filters={filters}
        total={query.data?.total || 0}
        isLoading={isLoading}
      />
      {After && (
        <Box pt={4}>
          <After
            items={items ?? []}
            filters={filters}
            total={query.data?.total || 0}
            isLoading={isLoading}
            params={afterProps}
          />
        </Box>
      )}
      {(!showPagination || (currentPage.pageSize === MAX_PAGE_SIZE)) ? null : (
        <Box pt={4}>
          <Pagination
            currentPage={currentPage.page}
            totalItems={totalItems}
            pageSize={currentPage.pageSize}
            onChange={updatePagination}
            onSizeChange={updatePagination}
          />
        </Box>
      )}
    </>
  );
}

DataView.displayName = "DataView";
export default DataView;
